import React from "react";
import BottomSheet from "../BottomSheet/BottomSheet";
import {
  AmountText,
  AmountWrapper,
  ArrowIcon,
  AssetIcon,
  AssetInformation,
  AssetWrapper,
  CryptoPreviewBottomSheetBodyContainer,
  DirectionText,
  ErrorContainer,
  ErrorText,
  FieldName,
  HeaderContainer,
  HeaderHandler,
  HeaderTitle,
  IconContainer,
  JIDRCurrency,
  RateContainer,
  Row,
  Separator,
  Value,
  Wrapper,
} from "./CryptoPreviewBottomSheet.styles";
import { PrimaryButton } from "../PrimaryButton/PrimaryButton";
import CountdownTimer from "../CountdownTimer/CountdownTimer";

type AssetInformationGroupProps = {
  iconUrl?: string;
  ticker?: string;
  amount?: string;
  isError?: boolean;
  fromLabel: string;
  receiveLabel: string;
};

const AssetInformationGroup: React.FC<AssetInformationGroupProps> = ({
  iconUrl,
  ticker = "JIDR",
  amount,
  isError,
  fromLabel,
  receiveLabel,
}) => {
  return (
    <AssetInformation>
      <IconContainer>
        {iconUrl ? <AssetIcon src={iconUrl} /> : <JIDRCurrency />}
      </IconContainer>
      {iconUrl ? (
        <DirectionText>{fromLabel}</DirectionText>
      ) : (
        <DirectionText>{receiveLabel}</DirectionText>
      )}
      <AmountWrapper>
        <AmountText $isError={isError}>{ticker.toUpperCase()}</AmountText>
        <AmountText $isError={isError}>{amount}</AmountText>
      </AmountWrapper>
    </AssetInformation>
  );
};

type HeaderProps = {
  title?: string;
};

const Header: React.FC<HeaderProps> = ({ title = "Preview top up" }) => (
  <HeaderContainer>
    <HeaderHandler />
    <HeaderTitle>{title}</HeaderTitle>
  </HeaderContainer>
);

type RateRowProps = {
  fieldName: string;
  value: string | React.ReactNode;
  boldValue?: boolean;
};

const RateRow: React.FC<RateRowProps> = ({
  fieldName,
  value,
  boldValue = false,
}) => (
  <Row>
    <FieldName>{fieldName}</FieldName>
    <Value $boldValue={boldValue}>{value}</Value>
  </Row>
);

export type CryptoPreviewBottomSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  fromAsset: string;
  fromAssetIconUrl: string;
  fromAmount: string;
  toAmount: string;
  rate: string;
  onGetDepositAddress: () => void;
  isError?: boolean;
  isButtonDisabled?: boolean;
  validTime?: number;
  onRefreshRate?: () => void;
  isRefreshing?: boolean;
  /** Label shown above the "from" asset (e.g. "From") */
  fromLabel: string;
  /** Label shown above the "receive" asset (e.g. "Receive") */
  receiveLabel: string;
  /** Title shown in the header and bottom sheet (e.g. "Preview top up") */
  previewTitle: string;
  /** Error message shown when minimum limit is not met */
  minimumLimitLabel: string;
  /** Field name for the exchange rate row (e.g. "Rate") */
  rateLabel: string;
  /** Field name for the rate validity countdown row (e.g. "Rate valid for") */
  rateValidForLabel: string;
  /** Field name for the total amount row (e.g. "Total amount") */
  totalAmountLabel: string;
  /** Button text for getting deposit address (e.g. "Get Deposit Address") */
  getDepositAddressLabel: string;
};

const CryptoPreviewBottomSheet: React.FC<CryptoPreviewBottomSheetProps> = ({
  isOpen,
  onClose,
  fromAssetIconUrl,
  fromAsset,
  fromAmount,
  toAmount,
  rate,
  onGetDepositAddress,
  isError,
  isButtonDisabled,
  validTime,
  onRefreshRate,
  isRefreshing,
  fromLabel,
  receiveLabel,
  previewTitle,
  minimumLimitLabel,
  rateLabel,
  rateValidForLabel,
  totalAmountLabel,
  getDepositAddressLabel,
}) => {
  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={previewTitle}
      height={"65%"}
      header={<Header title={previewTitle} />}
    >
      <CryptoPreviewBottomSheetBody>
        <Wrapper>
          <AssetWrapper>
            <AssetInformationGroup
              amount={fromAmount}
              iconUrl={fromAssetIconUrl}
              ticker={fromAsset}
              fromLabel={fromLabel}
              receiveLabel={receiveLabel}
            />
          </AssetWrapper>
          <ArrowIcon />
          <AssetWrapper>
            <AssetInformationGroup
              isError={isError}
              amount={toAmount}
              fromLabel={fromLabel}
              receiveLabel={receiveLabel}
            />
          </AssetWrapper>
        </Wrapper>

        <ErrorContainer>
          {isError && <ErrorText>{minimumLimitLabel}</ErrorText>}
          <Separator />
        </ErrorContainer>

        <RateContainer>
          <RateRow
            fieldName={rateLabel}
            value={`1 ${fromAsset} = ${rate} JIDR`}
          />
          {validTime && (
            <RateRow
              fieldName={rateValidForLabel}
              value={
                <CountdownTimer
                  validTime={validTime}
                  showRefreshButton={true}
                  onRefresh={onRefreshRate}
                  isRefreshing={isRefreshing}
                />
              }
            />
          )}

          <Separator />

          <RateRow
            fieldName={totalAmountLabel}
            value={`${toAmount} JIDR`}
          />
        </RateContainer>

        <PrimaryButton onClick={onGetDepositAddress} disabled={isButtonDisabled}>
          {getDepositAddressLabel}
        </PrimaryButton>
      </CryptoPreviewBottomSheetBody>
    </BottomSheet>
  );
};

const CryptoPreviewBottomSheetBody = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  return (
    <CryptoPreviewBottomSheetBodyContainer>
      {children}
    </CryptoPreviewBottomSheetBodyContainer>
  );
};

export default CryptoPreviewBottomSheet;
